import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { ContentTypeConfig, ContentTypeField } from "../../API/Models";
import { Modal } from "../../Components/Modal";
import TypeConnectionsButton from "../../Components/Connections/TypeConnectionsButton";
import { formatExactDate, formatRelativeTime } from "../../Helper/dates";
import type { ContentTypeEntry } from "../../Redux/Slice/ContentTypesSlice";
import { fetchContentTypeConfigs } from "../../Redux/Slice/ContentTypeConfigsSlice";
import type { IApplicationState } from "../../Redux/Reducers";
import { buildSystemFieldRows, isReservedTitle, SYSTEM_CONTENT_TYPE_KEYS } from "./utils";

/**
 * Content types are READ-ONLY in the admin. Their fields, flags and dynamic paths are defined in code
 * (APA.Site/ApaContentTypes) and served through the manifest overlay; the admin can edit a type's
 * records, not the type definition. This page is a viewer: it lists the code-defined types and lets
 * you inspect each type's fields. There is no create/edit/delete — those go through the CLI.
 */

/** Flattens a content type's fields (recursing nested list itemFields) into indented preview rows. */
type FieldRow = { path: string; field: ContentTypeField; depth: number };
const buildConfigFieldRows = (fields: ContentTypeField[] = [], parent = "", depth = 0): FieldRow[] => {
	const rows: FieldRow[] = [];
	fields.forEach((field) => {
		const key = field.key ?? "field";
		const path = parent ? `${parent}.${key}` : key;
		rows.push({ path, field, depth });
		if (field.itemFields && field.itemFields.length > 0) {
			rows.push(...buildConfigFieldRows(field.itemFields, path, depth + 1));
		}
	});
	return rows;
};

const ContentTypes: React.FC = () => {
	const [systemPreview, setSystemPreview] = useState<ContentTypeEntry | null>(null);
	const [configPreview, setConfigPreview] = useState<ContentTypeConfig | null>(null);

	const dispatch = useDispatch();
	const { items, loading, error, isRefreshing, lastUpdated, initialized } = useSelector(
		(state: IApplicationState) => state.contentTypeConfigs
	);
	const {
		items: systemContentTypes,
		loading: systemTypesLoading,
		error: systemTypesError
	} = useSelector((state: IApplicationState) => state.contentTypes);

	const systemTypeEntries = useMemo(
		() =>
			SYSTEM_CONTENT_TYPE_KEYS.map((key) => systemContentTypes[key]).filter(
				(entry): entry is ContentTypeEntry => Boolean(entry)
			),
		[systemContentTypes]
	);
	const missingSystemKeys = useMemo(
		() => SYSTEM_CONTENT_TYPE_KEYS.filter((key) => !systemContentTypes[key]),
		[systemContentTypes]
	);

	useEffect(() => {
		if (!initialized && !loading && !isRefreshing) {
			dispatch(fetchContentTypeConfigs() as any);
		}
	}, [dispatch, initialized, isRefreshing, loading]);

	const configPreviewRows = useMemo(
		() => buildConfigFieldRows(configPreview?.fields ?? []),
		[configPreview]
	);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Content Types</p>
						<h1 className="text-2xl font-semibold text-slate-900">Content types</h1>
						<p className="text-sm text-slate-500">
							Defined in code and read-only. Edit a type&rsquo;s records, not the type itself; type changes go through the CLI.
						</p>
						{lastUpdated && (
							<p className="text-xs text-slate-500">
								Last sync: {formatExactDate(lastUpdated)} ({formatRelativeTime(lastUpdated)})
							</p>
						)}
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-3">
						<div className="text-sm text-slate-500">{items.length} total</div>
						<button
							type="button"
							onClick={() => dispatch(fetchContentTypeConfigs(true) as any)}
							disabled={isRefreshing}
							className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{isRefreshing ? "Refreshing…" : "Refresh"}
						</button>
					</div>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
						<div>
							<p className="text-sm font-medium uppercase tracking-wide text-slate-500">System content types</p>
							<p className="text-sm text-slate-500">
								Built-in types provided by the codebase. These cannot be edited or deleted.
							</p>
						</div>
						<div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
							Locked
						</div>
					</header>
					{systemTypesError && (
						<div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
							{systemTypesError}
						</div>
					)}
					{missingSystemKeys.length > 0 && !systemTypesLoading && (
						<div className="m-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
							Missing definitions: {missingSystemKeys.join(", ")}.
						</div>
					)}
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="px-5 py-3">Key</th>
									<th className="px-5 py-3">Title</th>
									<th className="px-5 py-3">Description</th>
									<th className="px-5 py-3">Unique</th>
									<th className="px-5 py-3">Fields</th>
									<th className="px-5 py-3">Version</th>
									<th className="px-5 py-3 text-right">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{systemTypesLoading ? (
									<tr>
										<td colSpan={7} className="px-5 py-6 text-center text-slate-500">
											Loading system content types…
										</td>
									</tr>
								) : systemTypeEntries.length === 0 ? (
									<tr>
										<td colSpan={7} className="px-5 py-6 text-center text-slate-500">
											No system content types found.
										</td>
									</tr>
								) : (
									systemTypeEntries.map((entry) => (
										<tr key={entry.key} className="hover:bg-slate-50">
											<td className="px-5 py-3 text-xs font-mono text-slate-600">{entry.key}</td>
											<td className="px-5 py-3 text-slate-900">
												<div className="font-semibold">{entry.title || entry.key}</div>
											</td>
											<td className="px-5 py-3 text-slate-600">{entry.description || "—"}</td>
											<td className="px-5 py-3 text-slate-600">{entry.isSingleton ? "Yes" : "No"}</td>
											<td className="px-5 py-3 text-slate-600">{entry.fields?.length ?? 0}</td>
											<td className="px-5 py-3 text-slate-500">{entry.version || "—"}</td>
											<td className="px-5 py-3 text-right">
												<button
													type="button"
													onClick={() => setSystemPreview(entry)}
													className="text-sm font-semibold text-slate-600 hover:text-slate-900"
												>
													View fields
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
						<div>
							<p className="text-sm font-medium uppercase tracking-wide text-slate-500">Library</p>
							<p className="text-sm text-slate-500">
								Showing {items.length} content type{items.length === 1 ? "" : "s"}, defined in code.
							</p>
						</div>
						<div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
							Read-only
						</div>
					</header>
					{error && (
						<div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
					)}
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="px-5 py-3">Key</th>
									<th className="px-5 py-3">Id</th>
									<th className="px-5 py-3">Title</th>
									<th className="px-5 py-3">Description</th>
									<th className="px-5 py-3">Main</th>
									<th className="px-5 py-3">Taxonomy</th>
									<th className="px-5 py-3">Categories</th>
									<th className="px-5 py-3">Dynamic path</th>
									<th className="px-5 py-3">Fields</th>
									<th className="px-5 py-3">Translated</th>
									<th className="px-5 py-3">Updated</th>
									<th className="px-5 py-3 text-right">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={12} className="px-5 py-6 text-center text-slate-500">
											Loading content types…
										</td>
									</tr>
								) : items.length === 0 ? (
									<tr>
										<td colSpan={12} className="px-5 py-10 text-center text-slate-500">
											No content types found.
										</td>
									</tr>
								) : (
									items.map((item, index) => (
										<tr key={item.id ?? item.title ?? index} className="hover:bg-slate-50">
											<td className="px-5 py-3 text-xs font-mono font-semibold text-slate-800">{item.key ?? "—"}</td>
											<td className="px-5 py-3 text-xs font-mono text-slate-500">{item.id ?? "—"}</td>
											<td className="px-5 py-3 text-slate-900">
												<div className="flex items-center gap-2 font-semibold">
													<span>{item.title || "Untitled"}</span>
													{isReservedTitle(item.title) && (
														<span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
															Locked
														</span>
													)}
												</div>
											</td>
											<td className="px-5 py-3 text-slate-600">
												{item.description ? item.description : "—"}
											</td>
											<td className="px-5 py-3 text-slate-600">{item.isMain ? "Yes" : "No"}</td>
											<td className="px-5 py-3 text-slate-600">{item.supportsTaxonomy ? "Yes" : "No"}</td>
											<td className="px-5 py-3 text-slate-600">{item.supportsCategory ? "Yes" : "No"}</td>
											<td className="px-5 py-3 text-slate-600">{item.supportsDynamicPath ? "Yes" : "No"}</td>
											<td className="px-5 py-3 text-slate-600">{item.fields?.length ?? 0}</td>
											<td className="px-5 py-3">
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
														item.shouldBeTranslated ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
													}`}
												>
													{item.shouldBeTranslated ? "Yes" : "No"}
												</span>
											</td>
											<td className="whitespace-nowrap px-5 py-3 text-slate-500">
												<div className="text-xs font-semibold text-slate-900">
													{item.updatedAt ? formatExactDate(item.updatedAt) : "—"}
												</div>
												<div className="text-[11px] uppercase tracking-wide text-slate-400">
													{item.updatedAt ? formatRelativeTime(item.updatedAt) : ""}
												</div>
											</td>
											<td className="px-5 py-3 text-right">
												<div className="inline-flex items-center justify-end gap-2">
													{item.id && (
														<TypeConnectionsButton typeKey={item.id} typeLabel={item.title ?? item.id} />
													)}
													<button
														type="button"
														className="text-sm font-semibold text-slate-600 hover:text-slate-900"
														onClick={() => setConfigPreview(item)}
													>
														View fields
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>
			</div>

			<Modal show={!!systemPreview} onHide={() => setSystemPreview(null)} size="xl" centered>
				<Modal.Header closeButton className="bg-slate-50">
					<div className="space-y-1">
						<Modal.Title>{systemPreview?.title || systemPreview?.key || "System content type"}</Modal.Title>
						<p className="text-sm text-slate-500">
							Read-only fields defined by the system content type.
						</p>
					</div>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<div className="grid gap-2 text-sm text-slate-700">
						<div><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Key</span> {systemPreview?.key}</div>
						<div><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span> {systemPreview?.description || "—"}</div>
						<div><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unique</span> {systemPreview?.isSingleton ? "Yes" : "No"}</div>
					</div>
					<div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
								<tr>
									<th className="px-4 py-3 text-left">Path</th>
									<th className="px-4 py-3 text-left">Field</th>
									<th className="px-4 py-3 text-left">Type</th>
									<th className="px-4 py-3 text-left">Required</th>
									<th className="px-4 py-3 text-left">Description</th>
									<th className="px-4 py-3 text-left">Options</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{buildSystemFieldRows(systemPreview?.fields ?? []).length === 0 ? (
									<tr>
										<td colSpan={6} className="px-4 py-6 text-center text-slate-500">
											No fields defined.
										</td>
									</tr>
								) : (
									buildSystemFieldRows(systemPreview?.fields ?? []).map((row, index) => (
										<tr key={`${row.path}-${index}`}>
											<td className="px-4 py-3 text-xs font-mono text-slate-600">{row.path}</td>
											<td className="px-4 py-3">
												<div className="text-slate-900 font-semibold" style={{ paddingLeft: `${row.depth * 12}px` }}>
													{row.label}
												</div>
											</td>
											<td className="px-4 py-3 text-slate-600">{row.kind}</td>
											<td className="px-4 py-3 text-slate-600">{row.required ? "Yes" : "No"}</td>
											<td className="px-4 py-3 text-slate-600">{row.description}</td>
											<td className="px-4 py-3 text-slate-600">
												{row.options.length > 0 ? row.options.join(", ") : "—"}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>

			<Modal show={!!configPreview} onHide={() => setConfigPreview(null)} size="xl" centered>
				<Modal.Header closeButton className="bg-slate-50">
					<div className="space-y-1">
						<Modal.Title>{configPreview?.title || configPreview?.id || "Content type"}</Modal.Title>
						<p className="text-sm text-slate-500">
							Read-only fields defined in code. Edit records of this type, not the type itself.
						</p>
					</div>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
						<div><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Key</span> <span className="font-mono font-semibold">{configPreview?.key || "—"}</span></div><div><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Id</span> <span className="font-mono text-slate-500">{configPreview?.id || "—"}</span></div>
						<div><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dynamic path</span> {configPreview?.supportsDynamicPath ? "Yes" : "No"}</div>
						<div className="sm:col-span-2"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span> {configPreview?.description || "—"}</div>
					</div>
					<div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
								<tr>
									<th className="px-4 py-3 text-left">Field</th>
									<th className="px-4 py-3 text-left">Key</th>
									<th className="px-4 py-3 text-left">Type</th>
									<th className="px-4 py-3 text-left">Required</th>
									<th className="px-4 py-3 text-left">In list</th>
									<th className="px-4 py-3 text-left">Options</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{configPreviewRows.length === 0 ? (
									<tr>
										<td colSpan={6} className="px-4 py-6 text-center text-slate-500">
											No fields defined.
										</td>
									</tr>
								) : (
									configPreviewRows.map((row) => (
										<tr key={row.path}>
											<td className="px-4 py-3">
												<div className="text-slate-900 font-semibold" style={{ paddingLeft: `${row.depth * 12}px` }}>
													{row.field.title || row.field.key}
												</div>
											</td>
											<td className="px-4 py-3 text-xs font-mono text-slate-600">{row.field.key}</td>
											<td className="px-4 py-3 text-slate-600">{row.field.type || "—"}</td>
											<td className="px-4 py-3 text-slate-600">{row.field.condition?.required ? "Yes" : "No"}</td>
											<td className="px-4 py-3 text-slate-600">{row.field.showInList ? "Yes" : "No"}</td>
											<td className="px-4 py-3 text-slate-600">
												{row.field.options && row.field.options.length > 0
													? row.field.options.map((o) => o.label || o.value).join(", ")
													: "—"}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default ContentTypes;
